/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import isUndefined from 'lodash.isundefined';
import {Roles, User, Users} from '@ion/core/src/types/entities';
import {BulkEditorType, getEditedItems} from './BulkEditorUtils';
import {getRoles} from './UserEditorUtils';

// enum for bulk edit user form fields ...
export enum UserFormFields {
  Groups = 'groups',
  Status = 'status',
  EndDate = 'endDate',
  Role = 'role'
}

// defines the prop definitions for form state event target ...
export interface TargetType {
  name: UserFormFields,
  value: unknown;
  required?: boolean
}

const required = {required: true};
const notRequired = {required: false};

/**
* Provides the initial state of the user editor form, this state includes all named
* editor fields and their requirements, as well as any initial values required.
*/
export const getInitialState = (user: User, users: Users, roles: Roles) => {
  return {
    [UserFormFields.Groups]  : notRequired,
    [UserFormFields.Status]  : notRequired,
    [UserFormFields.Role]    : notRequired,
    [UserFormFields.EndDate] : notRequired,
    ...user,
    items                    : users,
    augmentor                : roles
  };
};

/**
* @serializeRole
*
* This function serializes the role entity.
*
* @param {BulkEditorType} serialized: The form data that needs to be transformed into
*  an User that can be passed to ctc to update the collection of Users.
* @param {User} user: The user data that needs to be transformed.
*/
function serializeRole(serialized: BulkEditorType, user: User, augmentor: Roles) {
  serialized.roles = getRoles(user, serialized.role, augmentor);
  return ({...user, ...serialized});
}

/**
* @serialize
*
* This function takes the form data from the user form and
* serializes it and gets passed to the service call.
*
* @param {BulkEditorType} state: The form data that needs to be transformed into
*   an User that can be passed to ctc to update the collection of Users.
*/
export const serialize = (state: BulkEditorType): Users => {
  const {items, augmentor} = state;
  const serialized: BulkEditorType = {items, augmentor};
  for (const [key, {value}] of Object.entries(state)) {
    if (!isUndefined(value)) {
      if (UserFormFields.Groups === key) {
        serialized.account = value;
      } else if (UserFormFields.EndDate === key) {
        serialized.terminationDate = value;
      } else {
        serialized[key] = value;
      }
    }
  }
  return getEditedItems(serialized, serializeRole);
};
